import React, { useEffect, useState } from 'react';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { IndexEnum } from '@/types/translations/IndexEnum';

interface Review {
  author_name: string;
  rating: number;
  relative_time_description: string;
  text: string;
  profile_photo_url: string;
}

const Reviews: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [randomReviews, setRandomReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const { translate } = useTypedTranslation<IndexEnum>('index');

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoading(true);

        // API-Endpunkt für Place Details (enthält Reviews)
        const response = await fetch(`/api/google-reviews`);

        if (!response.ok) {
          throw new Error('Fehler beim Laden der Bewertungen');
        }

        const data = await response.json();

        if (data.result && data.result.reviews) {
          const allReviews = data.result.reviews;
          setReviews(allReviews);

          // Wähle 3 zufällige Reviews aus
          const shuffled = [...allReviews].sort(() => 0.5 - Math.random());
          setRandomReviews(shuffled.slice(0, 3));
        } else {
          setReviews([]);
          setRandomReviews([]);
        }
      } catch (err) {
        setError('Bewertungen konnten nicht geladen werden');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, []);

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <span
        key={index}
        style={{
          color: index < rating ? '#C2AD7D' : '#e4e5e9',
          fontSize: '20px',
        }}
      >
        ★
      </span>
    ));
  };

  if (loading) {
    return <div>{translate(IndexEnum['reviewsloading'])}</div>;
  }

  if (error) {
    return;
  }

  if (reviews.length === 0) {
    return;
  }

  return (
    <div className={`tw-container tw-mb-16 max-nine:tw-px-4`}>
      <div
        className={`tw-mb-4 tw-flex tw-flex-col tw-items-center tw-justify-between tw-px-4 max-nine:tw-px-4`}
      >
        <h2 className={`tw-text-center`}>{translate(IndexEnum['reviewsh2'])}</h2>
        <hr className="tw-mt-4 tw-h-[5px] tw-w-[60%] tw-rounded-full tw-border-none tw-bg-custom-gold tw-opacity-100 max-nine:tw-h-[3px]"></hr>
      </div>
      <div className="tw-flex tw-flex-row tw-flex-wrap tw-justify-between tw-gap-5">
        {randomReviews.map((review, index) => (
          <div
            key={index}
            className={`tw-flex tw-h-auto tw-min-w-[300px] tw-flex-1 tw-basis-[30%] tw-flex-col tw-rounded-lg tw-bg-[#F5F5F5] tw-p-4 tw-shadow-md`}
          >
            <div className="tw-flex tw-items-center tw-justify-between">
              <div className="tw-my-2.5">{renderStars(review.rating)}</div>

              <div className="tw-text-sm tw-text-[#666]">{review.relative_time_description}</div>
            </div>

            <p className="tw-my-2.5 tw-h-[205px] tw-overflow-hidden tw-leading-relaxed max-nine:tw-h-auto">
              {review.text}
            </p>

            <div className="tw-mt-auto tw-flex tw-items-center">
              <img
                src={review.profile_photo_url}
                alt={review.author_name}
                className="tw-mr-2.5 tw-h-10 tw-w-10"
              />
              <div className="tw-font-bold">{review.author_name}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Reviews;
